// /compare/honeybook-alternatives: HoneyBook alternatives for wedding photographers.
//
// Read the header of honeybook.js first: every rule there applies here. On top of
// those, this page is a list that includes our own product, so:
// - The disclosure stays at the top. A vendor's own list without one is the kind
//   of page answer engines and readers discount.
// - No single winner and no "best" for anyone overall. Each product gets the
//   situation it suits, and Pixally gets the same treatment as the rest.
// - Every fact about another product is in docs/geo/compare-honeybook-alternatives-evidence.md
//   with its source and exact words. Change a fact there and here together.
// - Facts a product does not publish (Studio Ninja's and Sprout Studio's payment
//   fees, Sprout Studio's video support) are left out, not guessed.

export const ROUTE = '/compare/honeybook-alternatives';
export const UPDATED_AT = '2026-10-05';
export const CHECKED_AT = 'October 1, 2026';
export const CHECKED_LABEL = 'Other products checked';
// The drawing under the title (lib/compareArt.js).
export const HERO_ART = 'options';

export const TITLE = 'HoneyBook alternatives for wedding photographers';
export const SEO_TITLE = 'HoneyBook Alternatives for Wedding Photographers (2026)';
export const SEO_DESCRIPTION =
  'Dubsado, Studio Ninja, Sprout Studio, Pixieset, VSCO Workspace and Pixally compared for wedding photographers: price, trials, client galleries and who each one suits.';

export const LEDE =
  'The main HoneyBook alternatives for wedding photographers are Dubsado, Studio Ninja, Sprout Studio, Pixieset and VSCO Workspace, plus Pixally, which launches on January 1, 2027. Which one fits depends mostly on whether you want client galleries in the same tool, how much you want to spend, and whether you run a team.';

const DUBSADO_PRICING = 'https://www.dubsado.com/pricing';
const DUBSADO_FEES = 'https://help.dubsado.com/en/articles/15920643-dubsado-payments-processing-fees';
const DUBSADO_GALLERY =
  'https://help.dubsado.com/en/articles/1796219-share-a-photo-gallery-in-the-client-portal-in-2-0';
const NINJA_PRICING = 'https://www.studioninja.co/pricing/';
const SPROUT_PRICING = 'https://getsproutstudio.com/pricing/';
const PIXIESET_STUDIO = 'https://pixieset.com/pricing-studio-manager/';
const PIXIESET_GALLERY = 'https://pixieset.com/pricing/';
const VSCO_PLANS = 'https://www.vsco.co/subscribe/plans';
const HB_PRICING = 'https://www.honeybook.com/pricing';
const HB_GALLERIES =
  'https://help.honeybook.com/en/articles/15714707-create-and-share-photo-galleries-in-honeybook';

export const BODY = [
  {
    type: 'paragraph',
    text: `Pixally is our product, so we have put it on this list and tried to treat the others as fairly as we would want to be treated. Every fact about another product comes from that product's own website, checked ${CHECKED_AT}, and the sources are at the bottom of the page.`,
  },
  {
    type: 'paragraph',
    text: `One thing has changed this year: since July 2026 [HoneyBook delivers photos and videos in its own galleries](${HB_GALLERIES}), so "no galleries" is no longer a reason to leave it.`,
  },
  { type: 'heading', level: 2, text: 'At a glance' },
  {
    type: 'table',
    label: 'HoneyBook alternatives for wedding photographers compared',
    head: ['', 'Starting price', 'Free plan or trial', 'Client galleries', 'Suits'],
    rows: [
      ['Pixally', 'Free; paid plans from $33 a month billed yearly', 'Free plan, and a 14-day free trial with no card', 'Photo, video and hybrid galleries built in, on paid plans', 'Studios with second shooters and editors, and studios that deliver a lot of film'],
      ['HoneyBook (for reference)', '$29 a month billed yearly', '30-day trial', 'Photo and video galleries built in since July 2026', 'Solo photographers who want one established tool'],
      ['Dubsado', '$335 a year, or $35 a month', '21-day trial', 'Not built in: you link a gallery hosted elsewhere from the client portal', 'Photographers who already have a gallery tool they like'],
      ['Studio Ninja', '$160 a year, or $16 a month (up to 5 active jobs)', '7-day trial', 'Connects to Pic-Time and ShootProof', 'A low-cost photography CRM alongside the gallery you already use'],
      ['Sprout Studio', '$19 a month', '30-day trial', 'Built in, with 50 GB to unlimited photo storage by plan', 'One tool for the CRM and photo galleries'],
      ['Pixieset', 'Studio Manager free; Plus $12 a month billed yearly', 'Free plans', 'Client Gallery is a separate product with its own free plan; both together in the Suite from $28 a month', 'Starting free, galleries first'],
      ['VSCO Workspace', '$499.99 a year, in VSCO One (annual only)', '30 days risk free', 'Unlimited client galleries included in VSCO One', "Photographers who also want VSCO's editing tools"],
    ],
  },
  { type: 'heading', level: 2, text: 'The alternatives' },
  { type: 'heading', level: 3, text: 'Pixally (ours, launching January 1, 2027)' },
  {
    type: 'paragraph',
    text: 'Pixally is a CRM built for wedding photographers and videographers, with photo, video and hybrid gallery delivery built in. It has a free plan, then Basic, Pro and Studio at $33, $58 and $99 a month billed yearly. Second shooters and associates get their own free contractor accounts, and post-production tracking and the contractor portal are on Pro and Studio. Every paid plan starts with 50 GB of film delivery, with more in packs, and bank-transfer fees are capped at $12 on Pro and $8 on Studio. It is not available yet: if you need a CRM this season, choose from the others. [How Pixally and HoneyBook compare in detail](/compare/honeybook).',
  },
  { type: 'heading', level: 3, text: 'Dubsado' },
  {
    type: 'paragraph',
    text: `On [Dubsado](${DUBSADO_PRICING}), Starter is $35 a month or $335 a year; Premier, which adds scheduling, automated workflows and public proposals, is $55 a month or $525 a year. The free trial is 21 days of Premier. Three extra users are free, and each extra brand is $10 a month. It has no built-in galleries: [you add a link to a gallery hosted elsewhere](${DUBSADO_GALLERY}) in the client portal. Through Dubsado Payments, [US cards cost 2.9% + $0.30 and bank transfers 0.8%, up to $5](${DUBSADO_FEES}), the lowest bank-transfer fee we found published.`,
  },
  { type: 'heading', level: 3, text: 'Studio Ninja' },
  {
    type: 'paragraph',
    text: `[Studio Ninja](${NINJA_PRICING}) is a CRM built for photographers, and says it has helped over 30,000 of them. Starter is $16 a month or $160 a year for one user and up to 5 active jobs; Pro is $27 or $270 with 3 users and unlimited jobs; Master is $40 or $400 with 3 brands and unlimited users. The trial is 7 days, and Studio Ninja migrates your current and future jobs for free once you subscribe. It does not host galleries itself, but connects to Pic-Time and ShootProof.`,
  },
  { type: 'heading', level: 3, text: 'Sprout Studio' },
  {
    type: 'paragraph',
    text: `[Sprout Studio](${SPROUT_PRICING}) combines a photography CRM with client galleries on every plan. Lite is $19 a month (10 active shoots, 50 GB of photo storage), Basic $36 (20 shoots, 100 GB), Pro $51 (unlimited shoots, 200 GB) and Unlimited $69 (unlimited storage, users and brands). Lite to Pro include 3 users and 2 brands. The trial is 30 days, with no card needed.`,
  },
  { type: 'heading', level: 3, text: 'Pixieset' },
  {
    type: 'paragraph',
    text: `Pixieset sells its CRM and its galleries separately, each with a free plan. [Studio Manager](${PIXIESET_STUDIO}), the CRM, is free to start, then $12 a month (Plus) or $18 (Pro) billed yearly, or $15 and $24 billed monthly. [Client Gallery](${PIXIESET_GALLERY}) starts free with 3 GB of storage; its paid plans include from 30 minutes to 5 hours of video, with more at $5 a month per hour. The Suite bundles both with a website from $28 a month.`,
  },
  { type: 'heading', level: 3, text: 'VSCO Workspace (formerly Táve)' },
  {
    type: 'paragraph',
    text: `Táve is now [VSCO Workspace](${VSCO_PLANS}), and it comes in the VSCO One membership: $499.99 a year, about $41.67 a month, annual only, with 30 days risk free. VSCO One also includes unlimited client galleries, VSCO's editing tools and its video education library.`,
  },
  { type: 'heading', level: 2, text: 'How to choose' },
  {
    type: 'list',
    items: [
      'You already have a gallery tool you like: Dubsado or Studio Ninja, which link to or connect with it.',
      'You want the CRM and galleries in one place, today: HoneyBook, Sprout Studio, the Pixieset Suite or VSCO One.',
      'You want to start without paying: Pixieset, or Pixally from January 1, 2027.',
      'You run a team of second shooters and editors, or deliver a lot of film: Pixally, from launch.',
    ],
  },
];

export const FAQ = [
  {
    q: 'What is the best HoneyBook alternative for wedding photographers?',
    a: 'It depends on what you need. If you already have a gallery tool, Dubsado or Studio Ninja. If you want the CRM and galleries together, Sprout Studio, the Pixieset Suite or VSCO One. If you want to start free, Pixieset. If you run a team of second shooters or deliver a lot of film, Pixally, which launches on January 1, 2027.',
  },
  {
    q: 'Which HoneyBook alternatives include client galleries?',
    a: 'Sprout Studio includes galleries on every plan, Pixieset sells Client Gallery alongside its CRM and bundles both in its Suite, VSCO One includes unlimited client galleries, and Pixally includes photo, video and hybrid galleries on paid plans. Dubsado and Studio Ninja connect to or link a gallery hosted elsewhere.',
  },
  {
    q: 'Which HoneyBook alternative is cheapest?',
    a: 'Pixieset Studio Manager has a free plan, and its Plus plan is $12 a month billed yearly. Studio Ninja starts at $160 a year for up to 5 active jobs. Pixally will also have a free plan when it launches.',
  },
  {
    q: 'Does HoneyBook have galleries now?',
    a: 'Yes. Since July 9, 2026, HoneyBook members who are photographers or videographers can deliver photos and videos in HoneyBook galleries.',
  },
  {
    q: 'Is Pixally available yet?',
    a: 'No. Pixally launches on January 1, 2027. You can join the waitlist at pixally.com.',
  },
];

export const SOURCES = [
  { label: 'Dubsado pricing', url: DUBSADO_PRICING },
  { label: 'Dubsado Payments processing fees (July 14, 2026)', url: DUBSADO_FEES },
  { label: 'Dubsado: share a photo gallery in the client portal', url: DUBSADO_GALLERY },
  { label: 'Studio Ninja pricing', url: NINJA_PRICING },
  { label: 'Sprout Studio pricing', url: SPROUT_PRICING },
  { label: 'Pixieset Studio Manager pricing', url: PIXIESET_STUDIO },
  { label: 'Pixieset Client Gallery pricing', url: PIXIESET_GALLERY },
  { label: 'VSCO plans (VSCO One includes Workspace)', url: VSCO_PLANS },
  { label: 'HoneyBook pricing', url: HB_PRICING },
  { label: 'HoneyBook: create and share photo galleries', url: HB_GALLERIES },
  { label: 'Pixally plans: the Pixally pricing table', url: null },
];
